import { useCallback, useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Card,
  Center,
  Flex,
  Icon,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { CheckIcon, ChevronRightIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { baseApi } from '@/utils/api';
import { unwrap } from '@/pages/courses/api';
import { toaster } from '@/components/ui/toaster';
import { Checkbox } from '@/components/ui/checkbox';
import { ProgressBar } from '@/components/ui/progress-bar';
import ContentDrawer from '@/pages/contents/content-drawer';
import { Course, CourseItem, CourseModule } from '@/lib/types';

// localStorage key for this course's anonymous session token. Session-per-
// course, so one browser can hold progress on several shared courses.
function sessionKey(referenceId: string): string {
  return `ae_course_session_${referenceId}`;
}

// Unauthenticated share view for a course, reached via /public/courses/:ref.
// Uses baseApi (no auth interceptor) — a stale token in localStorage would
// otherwise 401 the whole page.
//
// Open courses record progress against an anonymous session minted on the
// first completion; log-in courses render read-only with a sign-in prompt.
export default function PublicCourse() {
  const { referenceId = '' } = useParams<{ referenceId: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyItemId, setBusyItemId] = useState<number | null>(null);
  const [busyModuleId, setBusyModuleId] = useState<number | null>(null);
  const [openingItemId, setOpeningItemId] = useState<number | null>(null);
  // The item shown in the in-page drawer, with its public/content payload.
  const [viewing, setViewing] = useState<{ item: CourseItem; content: any } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    const token = localStorage.getItem(sessionKey(referenceId));
    const query = token ? `?session=${encodeURIComponent(token)}` : '';
    baseApi
      .get(`public/courses/${referenceId}${query}`)
      .then((res) => {
        if (!cancelled) setCourse(unwrap<Course>(res));
      })
      .catch(async (err: any) => {
        if (cancelled) return;
        // A stale/foreign session token 404s — retry once without it.
        if (err.response?.status === 404 && token) {
          localStorage.removeItem(sessionKey(referenceId));
          try {
            const res = await baseApi.get(`public/courses/${referenceId}`);
            if (!cancelled) setCourse(unwrap<Course>(res));
            return;
          } catch {
            /* fall through to the error state */
          }
        }
        setError(
          err.response?.status === 404
            ? 'This course is no longer available.'
            : (err.response?.data?.error ?? 'Failed to load course.'),
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [referenceId]);

  // ensureSession mints the anonymous session lazily — on the first
  // completion, not on page load, so merely opening the link never shows up
  // anywhere as activity.
  const ensureSession = useCallback(async (): Promise<string> => {
    const existing = localStorage.getItem(sessionKey(referenceId));
    if (existing) return existing;
    const res = await baseApi.post(`public/courses/${referenceId}/sessions`);
    const session = unwrap<{ session_token: string }>(res);
    localStorage.setItem(sessionKey(referenceId), session.session_token);
    return session.session_token;
  }, [referenceId]);

  const openItem = async (item: CourseItem) => {
    if (!item.content_reference_id) return;
    setOpeningItemId(item.id);
    try {
      const res = await baseApi.get(`public/content/${item.content_reference_id}`);
      const content = res.data.data;
      if (content) setViewing({ item, content });
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to open content',
        type: 'error',
      });
    } finally {
      setOpeningItemId(null);
    }
  };

  const markComplete = async (item: CourseItem) => {
    setBusyItemId(item.id);
    try {
      const token = await ensureSession();
      const res = await baseApi.post(
        `public/courses/${referenceId}/items/${item.id}/complete`,
        { session_token: token },
      );
      setCourse(unwrap<Course>(res));
      if (viewing?.item.id === item.id) setViewing(null);
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to mark complete',
        type: 'error',
      });
    } finally {
      setBusyItemId(null);
    }
  };

  const ackAssignment = async (module: CourseModule) => {
    setBusyModuleId(module.id);
    try {
      const token = await ensureSession();
      const res = await baseApi.post(`public/courses/${referenceId}/modules/${module.id}/ack`, {
        session_token: token,
      });
      setCourse(unwrap<Course>(res));
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to confirm assignment',
        type: 'error',
      });
    } finally {
      setBusyModuleId(null);
    }
  };

  if (isLoading) {
    return (
      <Center py={16}>
        <Spinner size='xl' />
      </Center>
    );
  }

  if (error || !course) {
    return (
      <Card.Root maxW='md' mx='auto'>
        <Card.Body>
          <Text fontSize='sm' color='fg.muted' textAlign='center' py={8}>
            {error ?? 'This course is no longer available.'}
          </Text>
        </Card.Body>
      </Card.Root>
    );
  }

  const requiresLogin = course.access_mode === 'login';
  const pct = course.item_count > 0 ? (course.completed_item_count / course.item_count) * 100 : 0;
  const brand = course.workspace_primary_color || undefined;

  return (
    <Card.Root minH='80vh' mx='auto' width='full' maxW='2xl' overflow='hidden'>
      {/* Branded band, mirroring the prototype's mobile frame header */}
      <Box bg={brand ?? 'fg.strong'} px={6} py={5} color='white'>
        <Flex align='center' justify='space-between' mb={4}>
          <Text fontWeight='900' fontStyle='italic' fontSize='sm' letterSpacing='-0.02em'>
            {course.workspace_name?.toUpperCase() || 'COACH HUB'}
          </Text>
          <Text
            fontFamily='heading'
            fontSize='2xs'
            fontWeight='700'
            textTransform='uppercase'
            letterSpacing='0.16em'
            opacity={0.7}
          >
            Course
          </Text>
        </Flex>
        <Text
          fontFamily='heading'
          fontSize='2xl'
          fontWeight='800'
          textTransform='uppercase'
          letterSpacing='-0.01em'
          lineHeight='1.15'
        >
          {course.name}
        </Text>
        {!requiresLogin && (
          <Flex align='center' gap={3} mt={3.5}>
            <Box flex='1' h='6px' borderRadius='full' bg='whiteAlpha.300' overflow='hidden'>
              <Box h='100%' w={`${pct}%`} bg='white' borderRadius='full' transition='width 0.3s' />
            </Box>
            <Text fontSize='xs' opacity={0.85}>
              {course.completed_item_count} of {course.item_count} complete
            </Text>
          </Flex>
        )}
      </Box>

      <Card.Body bg='bg' p={5}>
        {course.description && (
          <Text fontSize='sm' color='fg.muted' mb={4}>
            {course.description}
          </Text>
        )}

        {requiresLogin && (
          <Box
            bg='bg.panel'
            borderWidth='1px'
            borderColor='border'
            borderRadius='lg'
            p={5}
            mb={4}
            textAlign='center'
          >
            <Text fontSize='sm' color='fg' mb={3}>
              This course records who completed it, so it asks for a quick sign-in before
              progress is saved.
            </Text>
            <Button variant='primary' size='sm' asChild>
              <NavLink to='/auth/login'>Sign in to start</NavLink>
            </Button>
          </Box>
        )}

        <Text
          fontFamily='heading'
          fontSize='2xs'
          fontWeight='700'
          textTransform='uppercase'
          letterSpacing='0.16em'
          color='fg.muted'
          mb={2.5}
        >
          Course modules
        </Text>

        <Stack gap={2.5}>
          {(course.modules ?? []).map((module, index) => {
            const locked = requiresLogin ? index > 0 : module.is_locked;
            const open = !requiresLogin && !module.is_locked;
            return (
              <Box
                key={module.id}
                borderWidth='1px'
                borderColor='border'
                borderRadius='lg'
                bg='bg.panel'
                opacity={locked ? 0.6 : 1}
              >
                <Flex align='center' gap={3} px={4} py={3}>
                  <Center
                    w='26px'
                    h='26px'
                    borderRadius='full'
                    bg={module.is_completed ? 'success.soft' : 'bg.subtle'}
                    color={module.is_completed ? 'success' : 'fg'}
                    fontFamily='heading'
                    fontSize='xs'
                    fontWeight='700'
                    flexShrink={0}
                  >
                    {module.is_completed ? <Icon as={CheckIcon} w={3.5} h={3.5} /> : index + 1}
                  </Center>
                  <Box minW={0} flex='1'>
                    <Text fontSize='sm' fontWeight='600' color='fg.strong'>
                      {module.name || 'Untitled module'}
                    </Text>
                    <Text fontSize='xs' color='fg.muted'>
                      {(module.items ?? []).length}{' '}
                      {(module.items ?? []).length === 1 ? 'item' : 'items'}
                      {module.assignment_text && ' · assignment'}
                    </Text>
                  </Box>
                  {locked && <Icon as={LockClosedIcon} w={4} h={4} color='fg.subtle' />}
                </Flex>

                {open && (
                  <Box px={4} pb={4}>
                    {module.instructions && (
                      <Text fontSize='sm' color='fg.muted' mb={2.5}>
                        {module.instructions}
                      </Text>
                    )}
                    <Stack gap={2}>
                      {(module.items ?? []).map((item) => {
                        // Items are completed from inside the drawer, so an
                        // openable row is itself the click target.
                        // content_id goes null when the library item is deleted (the reference
                        // id is a snapshot), so it's the real availability signal.
                        const openable =
                          !!item.content_id && !!item.content_reference_id && !item.is_locked;
                        return (
                          <Flex
                            key={item.id}
                            as={openable ? 'button' : 'div'}
                            onClick={openable ? () => openItem(item) : undefined}
                            aria-busy={openingItemId === item.id}
                            w='full'
                            textAlign='left'
                            align='center'
                            gap={2.5}
                            px={3}
                            py={2.5}
                            borderWidth='1px'
                            borderColor='border'
                            borderRadius='md'
                            cursor={openable ? 'pointer' : undefined}
                            transition='border-color 0.15s, background 0.15s'
                            _hover={openable ? { borderColor: 'border.emphasized', bg: 'bg.subtle' } : undefined}
                            opacity={item.is_locked ? 0.55 : 1}
                          >
                            <Box minW={0} flex='1'>
                              <Text fontSize='sm' fontWeight='600' color='fg.strong' truncate>
                                {item.content_name}
                              </Text>
                              {item.content_type_name && (
                                <Text fontSize='xs' color='fg.muted'>
                                  {item.content_type_name}
                                </Text>
                              )}
                            </Box>
                            {item.is_completed ? (
                              <Badge
                                bg='success.soft'
                                color='success'
                                fontSize='2xs'
                                textTransform='uppercase'
                              >
                                Done
                              </Badge>
                            ) : item.is_locked ? (
                              <Icon as={LockClosedIcon} w={3.5} h={3.5} color='fg.subtle' />
                            ) : !openable ? (
                              // Removed from the library, so there's no drawer
                              // to complete it in — keep the learner unblocked.
                              <Button
                                variant='outline'
                                size='xs'
                                onClick={() => markComplete(item)}
                                loading={busyItemId === item.id}
                              >
                                Mark complete
                              </Button>
                            ) : null}
                            {openable &&
                              (openingItemId === item.id ? (
                                <Spinner size='xs' color='fg.subtle' />
                              ) : (
                                <Icon as={ChevronRightIcon} w={4} h={4} color='fg.subtle' />
                              ))}
                          </Flex>
                        );
                      })}
                    </Stack>

                    {module.assignment_text && (
                      <Box
                        borderWidth='1px'
                        borderColor='brand.emphasized'
                        bg='brand.soft'
                        borderRadius='md'
                        px={3.5}
                        py={3}
                        mt={2.5}
                      >
                        <Text fontSize='sm' color='fg' mb={2.5}>
                          {module.assignment_text}
                        </Text>
                        <Checkbox
                          checked={module.assignment_acked}
                          disabled={module.assignment_acked || busyModuleId === module.id}
                          icon={busyModuleId === module.id ? <Spinner size='xs' /> : undefined}
                          onCheckedChange={() => {
                            if (!module.assignment_acked) ackAssignment(module);
                          }}
                        >
                          <Text fontSize='sm'>I&apos;ve finished this assignment</Text>
                        </Checkbox>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>

        {!requiresLogin && course.completed_item_count === course.item_count && (
          <Box mt={4}>
            <ProgressBar value={100} label='Course complete — nice work.' valueLabel='100%' />
          </Box>
        )}

        <Text textAlign='center' fontSize='xs' color='fg.subtle' mt={6}>
          Powered by Coach Hub
        </Text>
      </Card.Body>

      <ContentDrawer
        isOpen={!!viewing}
        onClose={() => setViewing(null)}
        content={viewing?.content}
        drawerId='public-course'
        isPublic
        footer={
          viewing && !viewing.item.is_completed ? (
            <Flex align='center' gap={4} wrap='wrap'>
              <Button
                variant='primary'
                onClick={() => markComplete(viewing.item)}
                loading={busyItemId === viewing.item.id}
              >
                <Icon as={CheckIcon} w={4} h={4} />
                Mark complete
              </Button>
              <Text fontSize='sm' color='fg.muted'>
                Every content type is marked complete by hand — nothing auto-completes.
              </Text>
            </Flex>
          ) : undefined
        }
      />
    </Card.Root>
  );
}
